/**
 * Activity Log Tab - unified timeline of manual edits (with undo/redo)
 * and AI prompt runs for a given mapping or the whole canvas.
 */

import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import type {
  CanvasManualAdjustmentModel,
  CanvasPromptRunRecordModel,
} from '../../services/api';
import { parseTimestamp } from '../../utils/dataUtils';
import type { ActivityLogEntry } from './types';
import { categoryConfig, relativeTime, templateLabel } from './constants';
import ManualAdjustmentCard from './ManualAdjustmentCard';

export interface ActivityLogTabProps {
  manualAdjustments?: CanvasManualAdjustmentModel[];
  promptRuns?: CanvasPromptRunRecordModel[];
  selectedTargetField?: string | null;
  edgeFieldMap?: Record<string, string>;
  onUndo?: (id: string) => Promise<void>;
  onRedo?: (id: string) => Promise<void>;
  undoingId?: string | null;
  redoingId?: string | null;
}

export const ActivityLogTab: React.FC<ActivityLogTabProps> = ({
  manualAdjustments,
  promptRuns,
  selectedTargetField,
  edgeFieldMap,
  onUndo,
  onRedo,
  undoingId,
  redoingId,
}) => {
  const entries = useMemo(() => {
    const result: ActivityLogEntry[] = [];
    const fieldLower = selectedTargetField?.toLowerCase() ?? '';

    // Manual adjustments -> entries
    for (const adj of manualAdjustments ?? []) {
      if (fieldLower) {
        const matchesTarget = adj.target_id.toLowerCase().includes(fieldLower);
        const matchesRationale = (adj.rationale || '').toLowerCase().includes(fieldLower);
        const resolvedFields = (edgeFieldMap?.[adj.target_id] || '').toLowerCase();
        const matchesEdgeFields = resolvedFields.includes(fieldLower);
        if (!matchesTarget && !matchesRationale && !matchesEdgeFields) continue;
      }

      result.push({
        id: `adj-${adj.adjustment_id}`,
        timestamp: parseTimestamp(adj.created_at),
        category: adj.ai_generated ? 'ai_mapping' : 'manual_edit',
        title: `${adj.target_kind} · ${edgeFieldMap?.[adj.target_id] || adj.target_id}`,
        detail: adj.rationale || undefined,
        author: adj.author,
        aiGenerated: Boolean(adj.ai_generated),
        meta: adj.details,
        adjustment: adj,
      });
    }

    // Prompt runs -> entries
    for (const run of promptRuns ?? []) {
      if (fieldLower) {
        const hasSpecificTargets = run.regenerated_nodes.length > 0 || run.regenerated_edges.length > 0;
        if (hasSpecificTargets) {
          const touchesField = [
            ...run.regenerated_nodes,
            ...run.regenerated_edges,
          ].some(id => id.toLowerCase().includes(fieldLower));
          if (!touchesField) continue;
        }
      }

      const tokens = run.token_usage
        ? `${run.token_usage.total_tokens.toLocaleString()} tokens`
        : undefined;
      const detailParts = [
        run.model_id,
        tokens,
      ].filter(Boolean);

      result.push({
        id: `pr-${run.llm_run_id}`,
        timestamp: parseTimestamp(run.generated_at),
        category: 'ai_regeneration',
        title: templateLabel(run.prompt_template),
        detail: detailParts.length > 0 ? detailParts.join(' · ') : undefined,
        author: run.model_id || 'AI',
        aiGenerated: true,
        meta: {
          regenerated_nodes: run.regenerated_nodes,
          regenerated_edges: run.regenerated_edges,
        },
      });
    }

    // Sort newest first
    result.sort((a, b) => b.timestamp - a.timestamp);
    return result;
  }, [manualAdjustments, promptRuns, selectedTargetField, edgeFieldMap]);

  if (!entries.length) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          {selectedTargetField
            ? `No activity recorded for "${selectedTargetField}".`
            : 'No activity recorded.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.5} p={2}>
      <Typography variant="caption" color="text.secondary">
        {entries.length} event{entries.length !== 1 ? 's' : ''}
        {selectedTargetField ? ` for ${selectedTargetField}` : ''}
      </Typography>
      {entries.map((entry) => {
        if (entry.adjustment) {
          return (
            <ManualAdjustmentCard
              key={entry.id}
              adjustment={entry.adjustment}
              edgeFieldMap={edgeFieldMap}
              onUndo={onUndo}
              onRedo={onRedo}
              undoingId={undoingId}
              redoingId={redoingId}
            />
          );
        }

        const cfg = categoryConfig[entry.category];
        return (
          <Paper key={entry.id} variant="outlined" sx={{ p: 1.5 }}>
            <Stack spacing={0.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip size="small" label={cfg.label} color={cfg.color} variant="outlined" sx={{ fontSize: '0.7rem' }} />
                {entry.aiGenerated && <AutoAwesomeIcon sx={{ fontSize: 14, color: 'primary.main' }} />}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto !important' }}>
                  {relativeTime(entry.timestamp)}
                </Typography>
              </Stack>
              <Typography variant="subtitle2" sx={{ fontSize: '0.82rem' }}>
                {entry.title}
              </Typography>
              {entry.detail && (
                <Tooltip title={entry.detail} arrow>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontSize: '0.78rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 320,
                      cursor: 'help',
                    }}
                  >
                    {entry.detail}
                  </Typography>
                </Tooltip>
              )}
              {entry.author && (
                <Typography variant="caption" color="text.disabled">
                  {entry.author}
                </Typography>
              )}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
};

export default ActivityLogTab;
